import { Dispatch, FC, SetStateAction, useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import iclasses from '@pages/Admin/CRM/styles/input.module.css'
import jclasses from '@pages/Admin/CRM/styles/jsmodal.module.css'
import classes from './index.module.css'
import { Empty, Modal, Select, Tooltip } from 'antd'
import { GeneralCourseInformation, RefetchGeneralDataVariantType } from '../..'
import {
  CloseOutlined,
  EditOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons'
import Button from '@UI/New/Button'
import { ElClassFormDataType } from '../PageContent/PageContent'
import CreateGroupModal from '../../../Manage/tabs/GroupsPage/components/CreateGroupModal/CreateGroupModal'
import EditTeacherModal from '../EditTeacherModal/EditTeacherModal'
import { getInArr } from '@/helpers/getInArr'
import TariffSelect from './TariffSelect'
import { classApi } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { getApiErrorMessage } from '@/helpers/getApiErrorMessage'

interface CourseListProps {
  generalData: GeneralCourseInformation
  elClassFormData: ElClassFormDataType
  setElClassFormData: Dispatch<SetStateAction<ElClassFormDataType>>
  refetchGeneralData: (variant: RefetchGeneralDataVariantType) => void
}

const ADD_TO_ALL_TOOLTIP =
  'Добавляет или возвращает этот курс всем действующим ученикам класса. ' +
  'Используйте кнопку, если курс был ранее удалён у отдельных учеников.'

/** Ответ на запуск массовой операции */
type EnrollmentOperation = {
  operation_id: number
  total_count: number
  already_running: boolean
}

const getDirectionSortValue = (
  directionId: number | null | undefined,
  allDirections: GeneralCourseInformation['allDirections']
) => {
  const direction = allDirections?.find((dir) => dir.id === directionId)
  const sorting = direction?.sorting

  if (sorting === null || sorting === undefined) {
    return Number.MAX_SAFE_INTEGER
  }

  return Number(sorting)
}

const sortGroupsByDirectionOrder = (
  groups: ElClassFormDataType['groups'] = [],
  allDirections: GeneralCourseInformation['allDirections']
) =>
  [...groups].sort((group1, group2) => {
    const group1Sorting = getDirectionSortValue(
      group1?.direction?.id,
      allDirections
    )
    const group2Sorting = getDirectionSortValue(
      group2?.direction?.id,
      allDirections
    )

    if (group1Sorting !== group2Sorting) {
      return group1Sorting - group2Sorting
    }

    return Number(group1?.direction?.id ?? Number.MAX_SAFE_INTEGER) -
      Number(group2?.direction?.id ?? Number.MAX_SAFE_INTEGER)
  })

const getOrdersGroupFromGroups = (groups: ElClassFormDataType['groups'] = []) =>
  JSON.stringify(groups?.map((group) => group?.id).filter(Boolean))

const CourseList: FC<CourseListProps> = ({
  generalData,
  elClassFormData,
  setElClassFormData,
  refetchGeneralData,
}) => {
  const [stateModal, setStateModal] = useState<{
    open: boolean
    type?: 'createGroup' | 'editTeacher'
    group?: NonNullable<ElClassFormDataType['groups']>[number] | undefined
  }>({
    open: false,
    type: 'createGroup',
    group: undefined,
  })

  const addAlert = useAddAlert()
  /** direction_group_id курсов, по которым сейчас идёт массовое добавление */
  const [runningGroupIds, setRunningGroupIds] = useState<number[]>([])
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
    }
  }, [])

  const stopRunning = (groupId: number) => {
    if (!isMountedRef.current) return
    setRunningGroupIds((prev) => prev.filter((id) => id !== groupId))
  }

  /**
   * Операция выполняется в очереди, поэтому ждать результат в интерфейсе нечего:
   * достаточно убедиться, что она зафиксирована и поставлена. Дальше её доведёт
   * воркер, а если задача потеряется — cron поставит её заново.
   */
  const runAddCourseToAllStudents = async (
    groupId: number,
    courseName: string
  ) => {
    setRunningGroupIds((prev) => [...prev, groupId])

    try {
      const res = (await classApi.addCourseToAllStudents(
        Number(elClassFormData.id),
        { direction_group_id: groupId }
      )) as any

      if (res?.response?.status >= 400 || res?.data?.status === 'error') {
        addAlert({
          type: 'error',
          title: 'Ошибка',
          text: getApiErrorMessage(res, 'Не удалось запустить добавление курса'),
        })
        return
      }

      const started: EnrollmentOperation | undefined = res?.data?.data

      addAlert({
        title: started?.already_running ? 'Уже выполняется' : 'Запущено',
        text: `Курс «${courseName}» добавляется ученикам класса (${
          started?.total_count ?? 0
        })`,
      })
    } catch (err) {
      addAlert({
        type: 'error',
        title: 'Ошибка',
        text: getApiErrorMessage(err, 'Не удалось добавить курс ученикам'),
      })
    } finally {
      stopRunning(groupId)
    }
  }

  const handleAddCourseToAllStudents = (
    group: NonNullable<ElClassFormDataType['groups']>[number]
  ) => {
    const groupId = Number(group?.id)
    if (!groupId) return

    const courseName = group?.direction?.name ?? '(без названия)'

    let confirmation: ReturnType<typeof Modal.confirm>
    confirmation = Modal.confirm({
      title: 'Добавить курс всем ученикам класса?',
      content: `Курс «${courseName}» будет добавлен всем действующим ученикам класса. Ранее удалённый вручную курс также будет возвращён.`,
      icon: null,
      centered: true,
      maskClosable: true,
      className: jclasses.modal,
      okText: 'Подтвердить',
      cancelText: 'Отменить',
      // Блок, а не стрелка-выражение: возвращённый из onOk промис antd ждёт,
      // держа модалку открытой. Здесь ждать нечего — операция уходит в очередь.
      onOk: () => {
        confirmation.destroy()
        void runAddCourseToAllStudents(groupId, courseName)
      },
    })
  }

  const handleAddGroup = () => {
    setElClassFormData({
      ...elClassFormData,
      groups: [
        ...(elClassFormData.groups || []),
        {
          key:
            (elClassFormData.groups?.length ?? 0) +
            1 +
            '_' +
            new Date().getTime(),
          id: null,
          isPersisted: false,
          direction: {
            id: null,
            tariffs: [],
          },
        },
      ],
    })
  }

  const isGroupSelectedInSameDirection = (
    currentKey: string | null | undefined,
    directionId: number | null | undefined,
    groupId: number | null | undefined
  ) => {
    if (!directionId || !groupId) {
      return false
    }

    return Boolean(
      elClassFormData.groups?.find(
        (group) =>
          group?.key !== currentKey &&
          group?.direction?.id === directionId &&
          group?.id === groupId
      )
    )
  }

  const handleSelectDirection = (
    key: string | null | undefined,
    directionId: number | null
  ) => {
    const groups = sortGroupsByDirectionOrder(
      elClassFormData.groups?.map((group) => {
        if (group?.key === key && group?.direction?.id !== directionId) {
          const direction = generalData.allDirections?.find(
            (dir) => dir.id === directionId
          )

          group = {
            key: group?.key,
            id: null,
            isPersisted: group?.isPersisted ?? false,
            direction: {
              id: directionId,
              tariffs: direction?.directionTariffs
                ?.filter((tariff) => getInArr(tariff.type, ['free', 'month']))
                ?.map((tariff) => ({
                  id: tariff.id,
                  name: tariff.name,
                })),
            },
          }
        }

        return group
      }),
      generalData.allDirections
    )

    setElClassFormData({
      ...elClassFormData,
      groups,
      orders_group: getOrdersGroupFromGroups(groups),
    })
  }

  const handleSelectGroup = (key: any, groupId: number | null | undefined) => {
    /*
     * Для порядка курсов используется group.id как уникальный идентификатор,
     * поэтому после смены группы пересобираем orders_group из текущего порядка строк.
     */

    const currentGroup = elClassFormData.groups?.find(
      (group) => group?.key === key
    )

    if (
      isGroupSelectedInSameDirection(key, currentGroup?.direction?.id, groupId)
    ) {
      return
    }

    const groups = sortGroupsByDirectionOrder(
      elClassFormData.groups?.map((group) => {
        if (group?.key === key) {
          if (group) {
            group.id = groupId
          } else {
            group = { id: groupId }
          }
        }

        return group
      }),
      generalData.allDirections
    )

    setElClassFormData({
      ...elClassFormData,
      groups,
      orders_group: getOrdersGroupFromGroups(groups),
    })
  }

  const handleSelectTariff = (
    key: any,
    tariffId: number | null | undefined
  ) => {
    setElClassFormData({
      ...elClassFormData,
      groups: elClassFormData.groups?.map((group) => {
        if (group?.key === key) {
          if (group) {
            group.tariff_id = tariffId
          }
        }

        return group
      }),
    })
  }

  if (!elClassFormData.groups || elClassFormData.groups.length === 0)
    return (
      <div className={classes.emptyList}>
        <div className={classes.emptyListImage}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={'Нет курсов'}
          />
        </div>
        <Button
          color="secondary"
          className={classes.borderRadiusLg}
          onClick={handleAddGroup}
          leftContent={
            <PlusOutlined
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          }
        >
          Добавить курс
        </Button>
      </div>
    )

  return (
    <>
      <div className={cn(classes.listWrapper, 'customScroll')}>
        <div className={cn(classes.list)}>
          <div className={classes.listHeader}>
            <p className={mclasses.label}>Курс</p>
            <p className={mclasses.label}>Группа</p>
            <p className={mclasses.label}>Педагог</p>
            <p className={mclasses.label}>Тариф</p>
            <p className={cn(mclasses.label, classes.massAddHeader)}>
              Ученики
              <Tooltip title={ADD_TO_ALL_TOOLTIP}>
                <InfoCircleOutlined
                  className={classes.massAddTooltipIcon}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              </Tooltip>
            </p>
            <span />
          </div>
          {elClassFormData.groups.map((group, i) => (
            <div
              key={group?.key ?? group?.id ?? i}
              className={classes.listItem}
            >
              {/* Направления / курсы */}
              <Select
                showSearch
                className={cn(iclasses.newSelect, classes.selectField)}
                popupClassName={iclasses.newSelectPopup}
                placeholder="Выберите курс"
                value={group?.direction?.id}
                onChange={(value) => handleSelectDirection(group?.key, value)}
                filterOption={(input, option) =>
                  (option?.label?.toLowerCase() ?? '').includes(
                    input.toLowerCase()
                  )
                }
                options={generalData.allDirections
                  ?.map(({ id: value, name: label }) => ({ label, value }))
                  ?.sort((a, b) => (a.label > b.label ? 1 : -1))}
              />
              {/* Группы */}
              <Select<number | string | null | undefined>
                showSearch
                className={cn(iclasses.newSelect, classes.selectField)}
                popupClassName={cn(
                  iclasses.newSelectPopup,
                  classes.selectWithCreateButton
                )}
                placeholder="Выберите группу"
                value={'' + (group?.id ?? '-')}
                onChange={(value, option) => {
                  if (value === 'create_group') {
                    setStateModal({
                      open: true,
                      type: 'createGroup',
                      group: group,
                    })
                  } else {
                    handleSelectGroup(group?.key, Number(value))
                  }
                }}
                filterOption={(input, option) =>
                  String(option?.label ?? '')
                    ?.toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={
                  group?.direction?.id
                    ? [
                        {
                          label: 'Создать группу',
                          value: 'create_group',
                          'data-create-element': true,
                        },
                        ...generalData.allGroups
                          ?.filter(
                            (g) =>
                              '' + g.direction_id ===
                                '' + group.direction?.id &&
                              !isGroupSelectedInSameDirection(
                                group?.key,
                                group?.direction?.id,
                                Number(g.id)
                              )
                          )
                          ?.map(({ id: value, name: label }) => ({
                            label,
                            value,
                          }))
                          ?.sort((a, b) => (a.label > b.label ? 1 : -1)),
                      ]
                    : []
                }
                notFoundContent={
                  group?.direction?.id
                    ? 'Нет доступных групп'
                    : 'Сначала выберите направление'
                }
              />
              {/* Педагог */}
              <button
                className={classes.buttonField}
                onClick={() => {
                  setStateModal({
                    open: true,
                    type: 'editTeacher',
                    group: group,
                  })
                }}
              >
                <p className={classes.buttonFieldText}>
                  {/* { group?.seminarian?.name ?? '-' } */}
                  {generalData.allGroups?.find(
                    (el) => el.id === String(group?.id)
                  )?.name &&
                  group?.id &&
                  group?.direction?.id ? (
                    <>
                      {
                        generalData.allGroups?.find(
                          (el) => el.id === String(group.id)
                        )?.teacher.name
                      }
                    </>
                  ) : (
                    '-'
                  )}
                </p>
                <span className={classes.buttonFieldIcon}>
                  <EditOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                </span>
              </button>
              {/* Тарифы */}
              <TariffSelect
                group={group}
                handleSelectTariff={handleSelectTariff}
                className={classes.selectField}
              />

              {/* Массовое добавление курса ученикам.
                  Показываем только у сохранённого курса: у несохранённой строки
                  ещё нет связи с классом, добавлять нечего. */}
              <div className={classes.massAddCell}>
                {group?.isPersisted && group?.id ? (
                  <Button
                    color="secondary"
                    className={classes.massAddButton}
                    isLoading={runningGroupIds.includes(Number(group.id))}
                    disabled={runningGroupIds.includes(Number(group.id))}
                    onClick={() => handleAddCourseToAllStudents(group)}
                    leftContent={
                      <UsergroupAddOutlined
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                      />
                    }
                  >
                    Добавить всем
                  </Button>
                ) : (
                  <span className={classes.massAddPlaceholder}>—</span>
                )}
              </div>

              {/* Actions */}
              <button
                className={classes.buttonActions}
                onClick={() =>
                  Modal.confirm({
                    title: 'Вы уверены что хотите удалить?',
                    content: `Группу "${
                      group?.name ?? '(без названия)'
                    }", курс "${group?.direction?.name ?? '(без названия)'}"`,
                    icon: null,
                    centered: true,
                    maskClosable: true,
                    className: jclasses.modal,
                    okText: 'Подтвердить',
                    cancelText: 'Отменить',
                    onOk: () => {
                      setElClassFormData({
                        ...elClassFormData,
                        groups: elClassFormData.groups?.filter(
                          (gr) => gr?.key !== group?.key
                        ),
                      })
                    },
                  })
                }
              >
                <CloseOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              </button>
            </div>
          ))}
        </div>
      </div>
      <Button
        color="secondary"
        className={cn(classes.borderRadiusLg, classes.addCourseButton)}
        onClick={handleAddGroup}
        leftContent={
          <PlusOutlined
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        }
      >
        Добавить курс
      </Button>

      {/* Modals */}
      <CreateGroupModal
        directionId={Number(stateModal.group?.direction?.id)}
        open={stateModal.open && stateModal.type === 'createGroup'}
        onClose={() => setStateModal({ open: false })}
        onSuccess={(groupId) => {
          handleSelectGroup(stateModal.group?.key, groupId)
          refetchGeneralData('groups')
          setStateModal({ open: false })
        }}
      />
      <EditTeacherModal
        group={stateModal.group}
        franchiseId={elClassFormData.franchise_id}
        open={stateModal.open && stateModal.type === 'editTeacher'}
        onClose={() => setStateModal({ open: false })}
        onSuccess={(teacher) => {
          refetchGeneralData('groups')
          // setElClassFormData(prevState => ({ ...prevState, groups: prevState.groups?.map(gr => gr?.id === stateModal.group?.id ? ({ ...gr, seminarian: teacher }) : gr) }))
          setStateModal({ open: false })
        }}
      />
    </>
  )
}

export default CourseList
